import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ElementClarifyingQuestion } from 'api/element-issues';
import { asSlicerPromotionQuestion } from './slicer-promotion';

export function hasUnansweredClarifications(questions: ElementClarifyingQuestion[]): boolean {
    return questions.some((q) => q.status === 'Unanswered');
}

// A slicer group is projected as Answered once nothing waits on it, but items
// still open in it are decisions the steward can take, so they keep the
// section open. A deferred-only group is not a question to answer: it stays
// reachable through the inconsistencies-card link (targetQuestionId), not by
// reopening the section on every load.
function slicerGroupHasOpenItems(q: ElementClarifyingQuestion): boolean {
    const group = asSlicerPromotionQuestion(q);
    return group != null && (group.items?.length ?? 0) > 0;
}

export function hasOpenClarifications(questions: ElementClarifyingQuestion[]): boolean {
    return questions.some((q) => q.status === 'Unanswered' || slicerGroupHasOpenItems(q));
}

interface ClarificationsVisibility {
    visible: boolean;
    hasUnanswered: boolean;
    close: () => void;
}

interface SessionState {
    targetKey: string;
    seenOpen: boolean;
    closed: boolean;
}

// Client-side only, like the rest of the panel's collapse state: a section
// whose questions were all settled before the panel opened never shows; one
// settled in this session stays with its all-answered state until closed.
// `targetKey` identifies the entity the questions belong to: the panel outlives
// SPA navigation, and one target's close or seen-open state must not leak into
// the next target's section.
export default function useClarificationsVisibility(
    questions: ElementClarifyingQuestion[],
    targetKey: string = '',
): ClarificationsVisibility {
    const targetQuestionId = useSelector((state: any) => state.concierge.targetQuestionId as string | null);
    const [stored, setStored] = useState<SessionState>({ targetKey, seenOpen: false, closed: false });
    const session: SessionState =
        stored.targetKey === targetKey ? stored : { targetKey, seenOpen: false, closed: false };

    const hasUnanswered = hasUnansweredClarifications(questions);
    const hasOpen = hasOpenClarifications(questions);
    const targeted = targetQuestionId != null && questions.some((q) => q.questionId === targetQuestionId);

    // Only a new unanswered question or a link into the section reopens a
    // closed one; a viewable slicer group alone must not, or close would be a
    // no-op on any model that carries one.
    useEffect(() => {
        if (hasUnanswered || targeted) {
            setStored({ targetKey, seenOpen: true, closed: false });
        }
    }, [hasUnanswered, targeted, targetKey]);

    const close = useCallback(
        () =>
            setStored((prev) => ({
                targetKey,
                seenOpen: prev.targetKey === targetKey ? prev.seenOpen : false,
                closed: true,
            })),
        [targetKey],
    );

    return {
        visible: targeted || (!session.closed && (hasOpen || session.seenOpen)),
        hasUnanswered,
        close,
    };
}
